/**
 * ACUTO PDKS kurulum + uygulama ekranları (ERP hesabı gerekmez).
 *  /pdks/kurulum#k=<kod>  → personele özel kurulum (GET/önizleme token TÜKETMEZ)
 *  /pdks/app              → PWA/APK açılış ekranı: ilk açılışta aktivasyon, sonra doğrudan QR kamera
 * Token yalnız /pdks/app içinde POST /pdks/mobile/activate başarıyla tamamlanınca tüketilir.
 * APK aktarımı: tek kullanımlık kısa süreli handoff + Android intent/App Link (?h=); Chrome depolamasına güvenilmez.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Smartphone, CheckCircle2, XCircle, ExternalLink, QrCode, AlertTriangle } from "lucide-react";
import { DEVICE_KEY } from "@/pages/PdksQrStamp";

const PENDING_KEY = "acuto:pdksPending";
const INSTALL_KEY = "acuto:pdksInstallId";
const UA = navigator.userAgent || "";
const isAndroid = /Android/i.test(UA);
const isWebView = /; wv\)|FBAN|FBAV|Instagram|Line\//.test(UA);
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;

export const installId = () => {
  let v = localStorage.getItem(INSTALL_KEY);
  if (!v) { v = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`; localStorage.setItem(INSTALL_KEY, v); }
  return v;
};
const deviceLabel = () => (UA.match(/\(([^)]+)\)/)?.[1] || UA).slice(0, 200);
const readCode = () => {
  const h = new URLSearchParams(window.location.hash.slice(1));
  return h.get("k") || new URLSearchParams(window.location.search).get("c") || "";
};

export function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center" data-testid="pdks-app-shell">
      <div className="text-xs uppercase tracking-widest text-blue-500 font-mono mb-1">ACUTO 360 · PDKS</div>
      <div className="w-full max-w-sm">{children}</div>
    </div>
  );
}
const Msg = ({ ok, text, testId }) => (
  <div className="space-y-4" data-testid={testId}>
    {ok ? <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" /> : <XCircle className="w-16 h-16 text-rose-500 mx-auto" />}
    <div className="text-base leading-relaxed">{text}</div>
  </div>
);

function usePwaInstall() {
  const [ready, setReady] = useState(!!window.__pdksInstallPrompt);
  const [installed, setInstalled] = useState(false);
  useEffect(() => {
    const r = () => setReady(true); const i = () => setInstalled(true);
    window.addEventListener("pdks-install-ready", r); window.addEventListener("appinstalled", i);
    return () => { window.removeEventListener("pdks-install-ready", r); window.removeEventListener("appinstalled", i); };
  }, []);
  const prompt = async () => {
    const e = window.__pdksInstallPrompt; if (!e) return;
    e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome === "accepted") { setInstalled(true); window.__pdksInstallPrompt = null; setReady(false); }
  };
  return { ready, installed, prompt };
}

// ---- /pdks/kurulum ----------------------------------------------------------
export function PdksSetup() {
  const [code] = useState(readCode);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(code ? "" : "Geçersiz kurulum bağlantısı. Yöneticinizden yeni bağlantı isteyin.");
  const [opening, setOpening] = useState(false);
  const pwa = usePwaInstall();
  const navigate = useNavigate();

  useEffect(() => {
    if (!code) return;
    api.post("/pdks/mobile/setup-info", { code })
      .then(({ data }) => { setInfo(data); localStorage.setItem(PENDING_KEY, JSON.stringify({ code, name: data.employee_name })); })
      .catch((e) => setError(formatApiError(e)));
  }, [code]);

  const openApk = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/pdks/mobile/handoff", { code });
      const fb = encodeURIComponent(window.location.href);
      window.location.href = `intent://${window.location.host}/pdks/app?src=apk&h=${encodeURIComponent(data.handoff)}#Intent;scheme=https;package=${data.app_package};S.browser_fallback_url=${fb};end`;
    } catch (e) { setError(formatApiError(e)); }
    finally { setTimeout(() => setOpening(false), 1500); }
  };

  if (error) return <Shell><Msg text={error} testId="setup-error" /></Shell>;
  if (!info) return <Shell><Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /></Shell>;
  const apkUrl = `${process.env.REACT_APP_BACKEND_URL}/api/pdks/mobile/apk`;
  return (
    <Shell>
      <div className="space-y-6 text-left" data-testid="pdks-setup-page">
        <div className="text-center">
          <div className="text-lg font-semibold">ACUTO PDKS Kurulum</div>
          <div className="text-2xl font-bold mt-3" data-testid="setup-employee-name">{info.employee_name}</div>
          {info.company_name && <div className="text-slate-400 text-sm">{info.company_name}</div>}
        </div>
        {isWebView && (
          <div className="flex gap-2 text-xs text-amber-300 bg-amber-950/40 border border-amber-800 rounded-sm p-3" data-testid="setup-webview-hint">
            <AlertTriangle className="w-4 h-4 shrink-0" />Kurulum için bu bağlantıyı Chrome'da açın (⋮ menü → "Chrome'da aç").
          </div>
        )}
        {pwa.installed ? (
          <div className="text-sm text-emerald-300 bg-emerald-950/40 border border-emerald-800 rounded-sm p-3" data-testid="setup-pwa-installed">
            ACUTO PDKS yüklendi. Ana ekrandaki <b>ACUTO PDKS</b> ikonuna dokunun; telefonunuz otomatik tanımlanacak.
          </div>
        ) : pwa.ready ? (
          <Button onClick={pwa.prompt} className="w-full h-14 text-base bg-blue-600 hover:bg-blue-500 rounded-md" data-testid="setup-install-pwa">
            <Smartphone className="w-5 h-5 mr-2" />ACUTO PDKS'yi Yükle
          </Button>
        ) : (
          <div className="text-xs text-slate-300 bg-slate-900 border border-slate-800 rounded-sm p-3 leading-relaxed" data-testid="setup-pwa-manual">
            <b>Uygulamayı yükleyin:</b> Chrome'da sağ üstteki ⋮ menüsünden <b>"Uygulamayı yükle"</b> veya <b>"Ana ekrana ekle"</b>yi seçin, ardından ana ekrandaki <b>ACUTO PDKS</b> ikonuna dokunun.
          </div>
        )}
        {info.apk_available && isAndroid && (
          <div className="space-y-2 border-t border-slate-800 pt-4" data-testid="setup-apk-section">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">veya Android uygulaması</div>
            <Button asChild variant="outline" className="w-full h-12 rounded-md border-slate-700" data-testid="setup-apk-download">
              <a href={apkUrl} download><Download className="w-4 h-4 mr-2" />ACUTO PDKS APK'yı İndir{info.apk_version ? ` (v${info.apk_version})` : ""}</a>
            </Button>
            <Button onClick={openApk} disabled={opening} variant="outline" className="w-full h-12 rounded-md border-slate-700" data-testid="setup-apk-open">
              {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" />Kurduktan sonra: Uygulamayı Aç ve Etkinleştir</>}
            </Button>
          </div>
        )}
        <button onClick={() => navigate("/pdks/app?src=browser")} className="block mx-auto text-xs text-slate-500 underline" data-testid="setup-use-browser">
          Uygulama yüklemeden bu tarayıcıda kullan
        </button>
        <div className="text-[11px] text-slate-600 text-center">Bağlantı yalnız size özeldir, {new Date(info.expires_at).toLocaleString("tr-TR")} tarihine kadar geçerlidir ve tek telefonda kullanılabilir.</div>
      </div>
    </Shell>
  );
}

// ---- /pdks/app --------------------------------------------------------------
function QrScanner({ onCode }) {
  const [err, setErr] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    let scanner; let disposed = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (disposed) return;
        scanner = new Html5Qrcode("pdks-qr-reader");
        ref.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } },
          (txt) => { scanner.pause?.(true); onCode(txt); }, () => {});
      } catch (e) { if (!disposed) setErr(e?.message || "Kamera açılamadı. Kamera iznini kontrol edin."); }
    })();
    return () => { disposed = true; const s = ref.current; if (s) s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch { /* */ } }); };
  }, [onCode]);
  return (
    <div className="space-y-3" data-testid="pdks-scanner">
      <div className="flex items-center justify-center gap-2 text-sm text-slate-300"><QrCode className="w-4 h-4 text-blue-400" />İşyerindeki QR kodu okutun</div>
      <div id="pdks-qr-reader" className="w-full aspect-square bg-black rounded-md overflow-hidden" />
      {err && <div className="text-rose-400 text-sm" data-testid="pdks-scanner-error">{err}</div>}
    </div>
  );
}

export function PdksApp() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [hasDevice, setHasDevice] = useState(() => !!localStorage.getItem(DEVICE_KEY));
  const [state, setState] = useState({});
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const h = params.get("h");
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (!h && (hasDevice || !pending)) return;
    const src = params.get("src");
    const platform = h ? "apk" : isStandalone() ? "pwa" : src === "browser" ? "browser" : "pwa";
    setState({ activating: true });
    api.post("/pdks/mobile/activate", { ...(h ? { handoff: h } : { code: pending.code }), install_id: installId(), platform, device_label: deviceLabel() })
      .then(({ data }) => {
        localStorage.setItem(DEVICE_KEY, data.token);
        localStorage.removeItem(PENDING_KEY);
        if (h) window.history.replaceState(window.history.state, "", "/pdks/app?src=apk");
        setState({ activated: data.employee?.name });
        setHasDevice(true);
      })
      .catch((e) => {
        if (e?.response?.status === 409 || e?.response?.status === 410) localStorage.removeItem(PENDING_KEY);
        if (h && localStorage.getItem(DEVICE_KEY)) { window.history.replaceState(window.history.state, "", "/pdks/app?src=apk"); setState({}); return; }
        setState({ error: formatApiError(e) });
      });
  }, []); // eslint-disable-line

  const onCode = useCallback((txt) => {
    try {
      const u = new URL(txt);
      const t = u.searchParams.get("t");
      if (u.pathname === "/pdks/qr" && t) { navigate(`/pdks/qr?t=${encodeURIComponent(t)}&app=1`); return; }
    } catch { /* geçersiz */ }
    setState({ error: "Bu bir ACUTO PDKS QR kodu değil. Terminal ekranındaki QR kodu okutun." });
  }, [navigate]);

  if (state.activating) return <Shell><Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /><div className="mt-4 text-sm text-slate-400">Telefonunuz tanımlanıyor…</div></Shell>;
  if (state.error) {
    return (
      <Shell>
        <Msg text={state.error} testId="pdks-app-error" />
        {hasDevice && <Button onClick={() => setState({})} className="mt-8 w-full h-12 bg-blue-600 hover:bg-blue-500" data-testid="pdks-app-retry">Tekrar Okut</Button>}
      </Shell>
    );
  }
  if (!hasDevice) return <Shell><Msg text="Bu telefon PDKS için tanımlı değil. Yöneticinizden size özel aktivasyon bağlantısını isteyin." testId="pdks-app-no-device" /></Shell>;
  return (
    <Shell>
      {state.activated && <div className="mb-4 text-sm text-emerald-300" data-testid="pdks-app-activated">Telefonunuz tanımlandı — {state.activated}</div>}
      <QrScanner onCode={onCode} />
    </Shell>
  );
}
